import { jsPDF } from 'jspdf';

/**
 * Creates a multi-page PDF from an array of base64 encoded image data URLs.
 * Each page in the PDF will have the exact dimensions of the image it contains.
 * @param images An array of image data URLs (e.g., from canvas.toDataURL()).
 * @returns A promise that resolves with the generated PDF as a Blob.
 */
export const createPdfFromImages = async (images: string[]): Promise<Blob> => {
    if (images.length === 0) {
        throw new Error("Nebyly poskytnuty žádné obrázky pro vytvoření PDF.");
    }

    // Get properties of the first image to initialize the PDF document correctly.
    const firstImageProps = await getImageProperties(images[0]);

    // Initialize jsPDF with the exact dimensions of the first image.
    // This creates the first page with the correct size from the start,
    // avoiding issues with the default A4 page format.
    const pdf = new jsPDF({
        orientation: firstImageProps.width > firstImageProps.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [firstImageProps.width, firstImageProps.height]
    });

    // Add the first image to the first (and currently only) page.
    pdf.addImage(images[0], 'JPEG', 0, 0, firstImageProps.width, firstImageProps.height);

    // Loop through the rest of the images, adding a new page for each.
    for (let i = 1; i < images.length; i++) {
        const image = images[i];
        const imgProps = await getImageProperties(image);
        
        // Add a new page with the exact dimensions of this image.
        // The orientation is implicitly handled by providing width and height.
        pdf.addPage([imgProps.width, imgProps.height]);
        
        // Add the image to fill the newly created page completely.
        pdf.addImage(image, 'JPEG', 0, 0, imgProps.width, imgProps.height);
    }

    return pdf.output('blob');
};

/**
 * Helper function to get image dimensions from a data URL.
 * @param dataUrl The image data URL.
 * @returns A promise that resolves with the width and height of the image.
 */
const getImageProperties = (dataUrl: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
            reject(new Error('Nepodařilo se načíst vlastnosti obrázku.'));
        };
        img.src = dataUrl;
    });
};